import { MySwal } from '../library/swal';
import { getApiErrorMessage } from './apiErrorMessage';

/**
 * Menampilkan kegagalan request ke user.
 *
 * Latar belakang: sebelumnya sebagian besar blok catch hanya memanggil
 * `console.log(err)`, sehingga ketika sebuah aksi gagal user tidak mendapat
 * tanda apa pun -- tombol seolah tidak bereaksi, atau tabel tampil kosong
 * tanpa penjelasan. Sisanya menampilkan `err.response.data.message` mentah
 * yang sering bernilai undefined.
 *
 * Ada dua tingkat notifikasi:
 * - notifyApiError     : aksi yang dipicu user (simpan/ubah/hapus/unggah).
 *                        Wajib disadari, jadi memakai modal.
 * - notifyApiLoadError : kegagalan memuat data saat halaman dibuka.
 *                        Tidak boleh memblokir layar, jadi memakai toast
 *                        yang hilang sendiri.
 */

// Satu halaman bisa memuat banyak data sekaligus. Bila server sedang mati,
// semua request gagal bersamaan dan user akan dibanjiri notifikasi yang isinya
// sama. Pesan yang identik karena itu ditahan sebentar.
const DEDUP_MS = 2000;
const recent = new Map();

const isDuplicate = (key) => {
  const now = Date.now();
  const last = recent.get(key);
  recent.set(key, now);
  if (recent.size > 50) {
    recent.forEach((t, k) => {
      if (now - t > DEDUP_MS) recent.delete(k);
    });
  }
  return Boolean(last && now - last < DEDUP_MS);
};

// Interceptor axios sudah menampilkan pesannya sendiri untuk 401 dan 403
// (sesi berakhir / akses ditolak / diblokir WAF). Jangan tampilkan dua kali.
const alreadyHandled = (error) => Boolean(error?.isHandledByInterceptor);

/** Kegagalan pada aksi yang dipicu user. Memakai modal. */
export const notifyApiError = (error, options = {}) => {
  const { title = 'Gagal Memproses', fallback, context } = options;
  const message = getApiErrorMessage(error, fallback);

  console.error(`[SPEAK]${context ? ' ' + context : ''} ${message}`, error);

  if (alreadyHandled(error)) return;
  if (isDuplicate(`aksi:${title}:${message}`)) return;

  MySwal.fire({
    icon: 'error',
    title,
    text: message,
  });
};

/** Kegagalan memuat data. Memakai toast supaya tidak memblokir halaman. */
export const notifyApiLoadError = (error, context) => {
  const message = getApiErrorMessage(
    error,
    'Data tidak dapat dimuat. Silakan muat ulang halaman.'
  );

  console.error(`[SPEAK]${context ? ' ' + context : ''} ${message}`, error);

  if (alreadyHandled(error)) return;
  if (isDuplicate(`muat:${message}`)) return;

  MySwal.fire({
    toast: true,
    position: 'top-end',
    icon: 'error',
    title: 'Gagal Memuat Data',
    text: message,
    showConfirmButton: false,
    timer: 6000,
    timerProgressBar: true,
  });
};

/**
 * Hanya mencatat ke console, tanpa notifikasi ke user.
 *
 * Dipakai untuk request yang berjalan berulang otomatis (polling notifikasi,
 * pemantauan job). Kalau request seperti ini ditampilkan sebagai notifikasi,
 * server yang sedang mati akan memunculkan notifikasi baru setiap satu putaran
 * polling -- mengganggu tanpa memberi informasi tambahan.
 */
export const logApiErrorQuietly = (error, context) => {
  const message = getApiErrorMessage(error);
  console.error(`[SPEAK][polling]${context ? ' ' + context : ''} ${message}`, error);
};

export default notifyApiError;
